import { URLS } from '@/lib/constants';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { NetworkError, UnknownApplicationError, ValidationError } from './errors';
import { logger } from './logger';
import { supabase } from './supabase';

export function ssoDebug(...args: unknown[]) {
  if (__DEV__) console.log('[SSO]', ...args);
}

export interface SSOExchangeResult {
  success: boolean;
  error?: Error;
}

export type WordPressSsoPayloadResult =
  | { ok: true; accessToken: string; email: string; supabaseUserId?: string }
  | { ok: false; error: Error };

const SSO_FETCH_TIMEOUT_MS = 10_000;

export async function fetchWordPressSsoPayload(
  ssoToken: string,
): Promise<WordPressSsoPayloadResult> {
  try {
    const supabaseUrl = Constants.expoConfig?.extra?.supabaseUrl;
    const supabaseAnonKey = Constants.expoConfig?.extra?.supabaseKey;

    if (!supabaseUrl || !supabaseAnonKey) {
      const error = new UnknownApplicationError(
        'SSO authentication is not properly configured. Please contact support.',
      );
      logger.error('Missing Supabase configuration for SSO token exchange');
      return { ok: false, error };
    }

    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/wordpress-sso`;
    ssoDebug('wordpress-sso: calling edge function');

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), SSO_FETCH_TIMEOUT_MS);

    let edgeResponse: Response;
    try {
      edgeResponse = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({ token: ssoToken }),
        signal: abortController.signal,
      });
    } catch (fetchError) {
      const aborted = (fetchError as { name?: string })?.name === 'AbortError';
      const error = new NetworkError(
        aborted
          ? 'Authentication server did not respond in time. Please try again.'
          : 'Failed to connect to authentication server. Please check your internet connection and try again.',
      );
      logger.error('Network error calling edge function:', fetchError);
      return { ok: false, error };
    } finally {
      clearTimeout(timeoutId);
    }

    if (!edgeResponse.ok) {
      ssoDebug('wordpress-sso: edge HTTP error', edgeResponse.status);
      let error: Error;
      if (edgeResponse.status >= 500) {
        error = new NetworkError(
          `Authentication server error (${edgeResponse.status}). Please try again in a moment.`,
        );
      } else if (edgeResponse.status === 401 || edgeResponse.status === 403) {
        error = new ValidationError(
          'Invalid or expired authentication token. Please try logging in again.',
        );
      } else {
        error = new UnknownApplicationError(
          `Authentication failed (${edgeResponse.status}). Please try again.`,
        );
      }
      logger.error('Supabase edge function failed:', edgeResponse.status);
      return { ok: false, error };
    }

    let edgeData: any;
    try {
      edgeData = await edgeResponse.json();
    } catch (parseError) {
      const error = new UnknownApplicationError(
        'Invalid response from authentication server. Please try again.',
      );
      logger.error('Failed to parse edge function response:', parseError);
      return { ok: false, error };
    }

    const email = String(edgeData?.user?.email ?? '').trim();
    ssoDebug('wordpress-sso: edge OK, WP user from response', {
      email,
      supabaseUserId: edgeData?.user?.id,
      method: edgeData?.method,
    });

    if (!edgeData.success || !edgeData.access_token) {
      const error = new ValidationError(
        'Authentication server returned an invalid response. The token may be expired or invalid. Please try logging in again.',
      );
      logger.error('Invalid authentication response from edge function:', edgeData);
      return { ok: false, error };
    }

    if (!email) {
      const error = new ValidationError(
        'Authentication response did not include an email. Please try logging in again.',
      );
      return { ok: false, error };
    }

    return {
      ok: true,
      accessToken: edgeData.access_token,
      email,
      supabaseUserId: edgeData?.user?.id,
    };
  } catch (error) {
    const appError = new UnknownApplicationError(
      `An unexpected error occurred during authentication: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
    );
    logger.error('Unexpected error during SSO payload fetch:', error);
    return { ok: false, error: appError };
  }
}

export function stripSsoTokenFromBrowserUrl(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has('sso_token')) return;
  url.searchParams.delete('sso_token');
  window.history.replaceState({}, '', url.toString());
}

export async function verifySsoMagicLinkToken(accessToken: string): Promise<SSOExchangeResult> {
  try {
    const { data: sessionData, error: verifyError } = await supabase.auth.verifyOtp({
      token_hash: accessToken,
      type: 'magiclink',
    });

    if (verifyError) {
      const error = new ValidationError(
        `Failed to verify authentication token: ${verifyError.message}. Please try logging in again.`,
      );
      logger.error('Failed to verify OTP:', verifyError);
      return { success: false, error };
    }

    if (!sessionData?.session) {
      const error = new UnknownApplicationError(
        'Authentication completed but no session was created. Please try again.',
      );
      logger.error('No session created after OTP verification');
      return { success: false, error };
    }

    ssoDebug('verifyOtp: session created for', sessionData.session.user?.email);
    logger.debug('SSO token exchange successful');
    return { success: true };
  } catch (error) {
    const appError = new UnknownApplicationError(
      `An unexpected error occurred during authentication: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
    );
    logger.error('Unexpected error during verifyOtp:', error);
    return { success: false, error: appError };
  }
}

export async function handleSSOTokenExchange(ssoToken: string): Promise<SSOExchangeResult> {
  const payload = await fetchWordPressSsoPayload(ssoToken);
  if (!payload.ok) {
    return { success: false, error: payload.error };
  }
  return verifySsoMagicLinkToken(payload.accessToken);
}

export function redirectToWordPressLogin(redirectPath?: string) {
  if (Platform.OS !== 'web') return;
  if (typeof window === 'undefined') return;

  const pathToRedirect = redirectPath || window.location.pathname;
  const encodedPath = encodeURIComponent(pathToRedirect);
  window.location.href = `${URLS.MY_ACCOUNT}?josephine_redirect=${encodedPath}`;
}
